import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Wallet, TrendingUp, BarChart3, Bitcoin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientPortfolios() {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolios();
  }, [token]);

  const fetchPortfolios = async () => {
    try {
      const response = await axios.get(`${API}/portfolios`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPortfolios(response.data);
    } catch (error) {
      console.error('Failed to fetch portfolios:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'available': return <Wallet className="w-6 h-6 text-[#D4AF37]" />;
      case 'trade':
      case 'trading': return <TrendingUp className="w-6 h-6 text-purple-500" />;
      case 'stock': return <BarChart3 className="w-6 h-6 text-blue-500" />;
      case 'crypto': return <Bitcoin className="w-6 h-6 text-purple-500" />;
      default: return <Wallet className="w-6 h-6" />;
    }
  };

  const getLabel = (type) => {
    switch (type) {
      case 'available': return t('portfolio_available');
      case 'trade':
      case 'trading': return 'Portefeuille Trading';
      case 'stock': return t('portfolio_stock');
      case 'crypto': return t('portfolio_crypto');
      default: return type;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'available': return '#D4AF37';
      case 'trade':
      case 'trading': return '#8B5CF6';
      case 'stock': return '#3B82F6';
      case 'crypto': return '#8B5CF6';
      default: return '#D4AF37';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  const totalCapital = portfolios.reduce((sum, p) => sum + (p.capital || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Total */}
        <Card className="card-premium">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t('total_capital')}</p>
                <p className="text-4xl font-bold text-[#D4AF37] mt-2">
                  €{totalCapital.toLocaleString()}
                </p>
              </div>
              <div className="w-16 h-16 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Wallet className="w-8 h-8 text-[#D4AF37]" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Portfolios Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {portfolios.map(portfolio => (
            <Card key={portfolio.id} className="card-premium" data-testid={`portfolio-${portfolio.portfolio_type}`}>
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${getColor(portfolio.portfolio_type)}15` }}
                  >
                    {getIcon(portfolio.portfolio_type)}
                  </div>
                  <div>
                    <CardTitle className="text-lg">{getLabel(portfolio.portfolio_type)}</CardTitle>
                    <p className="text-xs text-muted-foreground">{portfolio.currency}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('capital')}</p>
                    <p className="text-3xl font-bold" style={{ color: getColor(portfolio.portfolio_type) }}>
                      €{(portfolio.capital || 0).toLocaleString()}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
                    <div>
                      <p className="text-xs text-muted-foreground">{t('available')}</p>
                      <p className="text-lg font-semibold">€{(portfolio.available || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('bonus')}</p>
                      <p className="text-lg font-semibold">€{(portfolio.bonus || 0).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
